import React, { useEffect, useMemo } from 'react';
import { Alert, Card, Col, Form, FormInstance, Input, InputNumber, Row, Select, Space, Switch, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { OrderDto } from '../../../dto/order';
import { SupplierOrderItemDto, supplierOrderQuantityRule } from '../../../dto/supplierOrder';
import {
  ALLOCATION_UNITS,
  convertAllocationQuantity,
  resolveDimensionGeometryMm,
} from '../../../utils/dimensionConversion';
import { formatUnit } from '../../../utils/units';
import { SupplierOrderAllocationItemOption } from '../supplierOrderAllocationOptions';
import {
  buildExistingSupplierOrderItemAllocations,
  calculateSupplierItemAllocationBalance,
  type ExistingSupplierItemAllocationDraft,
} from '../supplierOrderCreateMode';
import SupplierOrderManualCostOverride from './SupplierOrderManualCostOverride';

type FieldName = 'allocateByItem' | 'supplierOrderItemId' | 'supplierOrderItemIndex' | 'allocatedQuantity' | 'allocatedUnit' | 'allocatedCostPLN';

interface SupplierOrderLinkAllocationFieldsProps {
  form: FormInstance;
  canEditSupplierOrderFinance: boolean;
  supplierOrderItemOptions: SupplierOrderAllocationItemOption[];
  namePrefix?: (string | number)[];
  itemFieldName?: 'supplierOrderItemId' | 'supplierOrderItemIndex';
  onSupplierOrderItemChange?: (value: string | number | null | undefined) => void;
  /**
   * New customer↔supplier links use the canonical item-allocation table.  The
   * flag is explicit because this shared component still serves historical
   * edit/create surfaces whose legacy header fields must remain readable.
   */
  canonicalItemAllocations?: boolean;
  customerOrder?: OrderDto | null;
  supplierOrderItems?: SupplierOrderItemDto[];
}

const SupplierOrderLinkAllocationFields: React.FC<SupplierOrderLinkAllocationFieldsProps> = ({
  form,
  canEditSupplierOrderFinance,
  supplierOrderItemOptions,
  namePrefix = [],
  itemFieldName = 'supplierOrderItemId',
  onSupplierOrderItemChange,
  canonicalItemAllocations = false,
  customerOrder = null,
  supplierOrderItems = [],
}) => {
  const { t, i18n } = useTranslation(['orders']);
  const fieldPath = (name: FieldName) => (namePrefix.length > 0 ? [...namePrefix, name] : name);
  const allocateByItem = Form.useWatch(fieldPath('allocateByItem'), form);
  const selectedItemValue = Form.useWatch(fieldPath(itemFieldName), form);
  const allocatedQuantity = Form.useWatch(fieldPath('allocatedQuantity'), form);
  const allocatedUnit = Form.useWatch(fieldPath('allocatedUnit'), form);
  const selectedItem = supplierOrderItemOptions.find(
    (option) => String(option.value) === String(selectedItemValue)
  );
  const targetUnit = selectedItem?.unit || null;
  const selectedItemDimensions = {
    width: selectedItem?.width == null ? null : Number(selectedItem.width),
    length: selectedItem?.length == null ? null : Number(selectedItem.length),
    height: selectedItem?.height == null ? null : Number(selectedItem.height),
    thickness: selectedItem?.thickness == null ? null : Number(selectedItem.thickness),
  };
  const shouldConvert = Boolean(
    selectedItem
    && allocatedQuantity
    && allocatedUnit
    && targetUnit
    && allocatedUnit !== targetUnit
  );
  const conversion = shouldConvert
    ? convertAllocationQuantity({
        quantity: allocatedQuantity,
        sourceUnit: allocatedUnit,
        targetUnit,
        ...selectedItemDimensions,
      })
    : null;
  const locale = i18n?.language?.startsWith('pl') ? 'pl-PL' : 'en-US';
  const formatQuantity = (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 3 });
  const formatDimension = (value: number | string | null | undefined) => (
    Number(value).toLocaleString(locale, { maximumFractionDigits: 2 })
  );
  const formatUnitForConversionError = (unit: string | null | undefined) => (
    unit === 'pcs' && locale === 'pl-PL' ? 'sztuki' : formatUnit(unit, i18n.language)
  );
  const isAreaVolumeConversion = (
    (allocatedUnit === 'm2' && targetUnit === 'm3')
    || (allocatedUnit === 'm3' && targetUnit === 'm2')
  );
  const dimensionGeometry = resolveDimensionGeometryMm(selectedItemDimensions);
  const dimensionValues = isAreaVolumeConversion
    ? [dimensionGeometry.depthMm]
    : allocatedUnit === 'm3' || targetUnit === 'm3'
      ? [
          dimensionGeometry.plane?.firstMm,
          dimensionGeometry.plane?.secondMm,
          dimensionGeometry.depthMm,
        ]
      : [dimensionGeometry.plane?.firstMm, dimensionGeometry.plane?.secondMm];
  const conversionMessage = conversion?.error === 'MISSING_DIMENSIONS'
    ? t('orders:supplierOrders.form.allocationConversionMissingDimensions', {
        sourceUnit: formatUnitForConversionError(allocatedUnit),
        targetUnit: formatUnitForConversionError(targetUnit),
      })
    : conversion?.error === 'UNSUPPORTED_CONVERSION'
      ? t('orders:supplierOrders.form.allocationConversionUnsupported', {
          sourceUnit: formatUnitForConversionError(allocatedUnit),
          targetUnit: formatUnitForConversionError(targetUnit),
      })
      : null;

  const canonicalMatch = useMemo(() => (
    customerOrder
      ? buildExistingSupplierOrderItemAllocations(customerOrder, supplierOrderItems)
      : { allocations: [], unmatchedCustomerProductNames: [] }
  ), [customerOrder, supplierOrderItems]);

  useEffect(() => {
    if (!canonicalItemAllocations) return;
    form.setFieldValue('itemAllocations', canonicalMatch.allocations);
  }, [canonicalItemAllocations, canonicalMatch.allocations, form]);

  if (canonicalItemAllocations) {
    if (!customerOrder) {
      return (
        <Alert
          type="info"
          showIcon
          message={t('orders:supplierOrders.form.selectCustomerOrder')}
          style={{ marginBottom: 16 }}
        />
      );
    }

    return (
      <>
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {t('orders:supplierOrders.allocation.automaticMatchHelp')}
        </Typography.Text>
        {canonicalMatch.unmatchedCustomerProductNames.length > 0 ? (
          <Alert
            type={canonicalMatch.allocations.length > 0 ? 'warning' : 'error'}
            showIcon
            message={canonicalMatch.allocations.length > 0
              ? t('orders:supplierOrders.allocation.unmatchedProducts', {
                  products: canonicalMatch.unmatchedCustomerProductNames.join(', '),
                })
              : t('orders:supplierOrders.allocation.noMatchingProducts')}
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Form.List
          name="itemAllocations"
          rules={[{
            validator: async (_, allocations) => {
              if (!Array.isArray(allocations) || allocations.length === 0) {
                throw new Error(t('orders:supplierOrders.allocation.noMatchingProducts'));
              }
            },
          }]}
        >
          {(fields, _operations, { errors }) => (
            <>
              {fields.map((field) => (
                <Card key={field.key} size="small" style={{ marginBottom: 12 }}>
                  {[
                    'orderProductId', 'supplierOrderItemId', 'customerProductName', 'supplierItemName',
                    'quantity', 'unit', 'customerUnit', 'width', 'length', 'height', 'thickness',
                    'surplusDisposition',
                  ].map((name) => (
                    <Form.Item key={name} name={[field.name, name]} hidden>
                      <Input />
                    </Form.Item>
                  ))}
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const allocation = getFieldValue(['itemAllocations', field.name]) as ExistingSupplierItemAllocationDraft;
                      const balance = calculateSupplierItemAllocationBalance(allocation || {});
                      const formatQuantity = (value: number | null) => value == null
                        ? '—'
                        : value.toLocaleString('pl-PL', { maximumFractionDigits: 3 });

                      return (
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                          <Typography.Text strong>{allocation?.customerProductName || '—'}</Typography.Text>
                          <Typography.Text type="secondary">
                            {t('orders:supplierOrders.allocation.supplierLine')}: {' '}
                            {allocation?.supplierItemName || '—'} · {' '}
                            {formatQuantity(Number(allocation?.quantity || 0))} {formatUnit(allocation?.unit)}
                          </Typography.Text>
                          <Row gutter={12} align="middle">
                            <Col xs={24} md={12}>
                              <Form.Item
                                name={[field.name, 'customerQuantity']}
                                label={t('orders:supplierOrders.allocation.customerTarget')}
                                rules={[{ required: true }, supplierOrderQuantityRule(t)]}
                                style={{ marginBottom: 0 }}
                              >
                                <InputNumber
                                  step={0.001}
                                  precision={3}
                                  addonAfter={formatUnit(allocation?.customerUnit)}
                                  style={{ width: '100%' }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Typography.Text type="secondary">
                                {t('orders:supplierOrders.allocation.marginQuantity')}
                              </Typography.Text>
                              <div style={{ marginTop: 4, fontWeight: 650 }}>
                                {formatQuantity(balance.allocatedSupplierQuantity)} {formatUnit(allocation?.unit)}
                              </div>
                            </Col>
                          </Row>
                          {balance.error ? (
                            <Typography.Text type="danger">
                              {t('orders:supplierOrders.allocation.missingDimensions')}
                            </Typography.Text>
                          ) : null}
                        </Space>
                      );
                    }}
                  </Form.Item>
                </Card>
              ))}
              <Form.ErrorList errors={errors} />
            </>
          )}
        </Form.List>
      </>
    );
  }

  return (
    <>
      <Form.Item name={fieldPath('allocateByItem')} valuePropName="checked">
        <Switch
          checkedChildren={t('orders:supplierOrders.form.allocateByItem')}
          unCheckedChildren={t('orders:supplierOrders.form.linkWholeOrder')}
        />
      </Form.Item>
      {allocateByItem && (
        <>
          <Form.Item
            name={fieldPath(itemFieldName)}
            label={t('orders:supplierOrders.form.supplierOrderItem')}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={supplierOrderItemOptions}
              onChange={onSupplierOrderItemChange}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={16}>
              <Form.Item
                name={fieldPath('allocatedQuantity')}
                label={t('orders:supplierOrders.form.allocatedQuantity')}
                rules={[supplierOrderQuantityRule(t)]}
              >
                <InputNumber precision={3} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item
                name={fieldPath('allocatedUnit')}
                label={t('orders:supplierOrders.form.allocatedUnit')}
                rules={[{
                  validator: async (_, value) => {
                    if (allocatedQuantity && !value) {
                      throw new Error(t('orders:supplierOrders.form.allocationUnitRequired'));
                    }
                    if (conversionMessage) {
                      throw new Error(conversionMessage);
                    }
                  },
                }]}
              >
                <Select
                  options={ALLOCATION_UNITS.map((unit) => ({
                    value: unit,
                    label: formatUnit(unit, i18n.language),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          {conversion?.value !== null && conversion?.value !== undefined && !conversion.error && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t(isAreaVolumeConversion
                ? 'orders:supplierOrders.form.allocationConversionThicknessHint'
                : 'orders:supplierOrders.form.allocationConversionHint', {
                sourceQuantity: formatQuantity(Number(allocatedQuantity)),
                sourceUnit: formatUnit(allocatedUnit, i18n.language),
                targetQuantity: formatQuantity(conversion.value),
                targetUnit: formatUnit(targetUnit, i18n.language),
                dimensions: dimensionValues.map(formatDimension).join(' × '),
                thickness: formatDimension(dimensionGeometry.depthMm),
              })}
            />
          )}
          {conversionMessage && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={conversionMessage}
            />
          )}
          <SupplierOrderManualCostOverride
            visible={canEditSupplierOrderFinance}
            name={fieldPath('allocatedCostPLN')}
            addonAfter="PLN"
          />
        </>
      )}
    </>
  );
};

export default SupplierOrderLinkAllocationFields;
